import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle } from "lucide-react";

export default function TestResult() {
  const [data, setData] = useState(null);
  const { t } = useLang();
  const nav = useNavigate();
  useEffect(() => {
    const raw = sessionStorage.getItem("last-result");
    if (!raw) { nav("/dashboard"); return; }
    setData(JSON.parse(raw));
  }, [nav]);
  if (!data) return null;
  const { attempt, details } = data;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <div className="border border-slate-200 bg-white p-10 mb-10 shadow-hard-black">
          <div className="label-mono text-[#002FA7] mb-2">— {t("result_score")}</div>
          <h1 className="font-display font-black text-6xl tracking-tighter">{attempt.score_percent}%</h1>
          <p className="text-slate-600 mt-4">{attempt.correct} corrette su {attempt.total} · Durata: {Math.floor(attempt.duration_seconds/60)}m {attempt.duration_seconds%60}s</p>
        </div>

        <h2 className="font-display font-bold text-2xl mb-6">{t("result_review")}</h2>
        <div className="space-y-4">
          {details.map((d, i) => (
            <div key={d.question_id} data-testid={`review-${i}`}
              className={`border p-6 bg-white ${d.is_correct ? "border-green-300" : "border-red-300"}`}>
              <div className="flex items-center gap-2 mb-3">
                {d.is_correct ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                <span className="label-mono">{i + 1}. {d.is_correct ? t("correct") : t("wrong")}</span>
              </div>
              <p className="whitespace-pre-line text-sm mb-3 text-slate-700">{d.question}</p>
              {d.table && (
                <div className="border border-slate-300 mb-3 overflow-x-auto text-xs">
                  <table className="w-full">
                    <thead className="bg-slate-100 label-mono">
                      <tr>{d.table.headers.map((h, i) => (<th key={i} className="text-left p-2 border-r last:border-r-0 border-slate-300">{h}</th>))}</tr>
                    </thead>
                    <tbody>
                      {d.table.rows.map((row, ri) => (
                        <tr key={ri} className="border-t border-slate-300">
                          {row.map((cell, ci) => (<td key={ci} className="p-2 border-r last:border-r-0 border-slate-200 font-mono-num">{cell}</td>))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {d.matrix && (
                <div className="mb-3 flex gap-4 items-center">
                  <div className="grid grid-cols-3 border border-slate-400" style={{ width: 210 }}>
                    {d.matrix.map((cell, i) => (
                      <div key={i} className={`aspect-square border border-slate-300 ${cell ? "bg-white" : "bg-slate-50 flex items-center justify-center"}`}>
                        {cell
                          ? <div className="w-full h-full p-1" dangerouslySetInnerHTML={{ __html: cell }} />
                          : <span className="text-xl font-black text-[#002FA7]">?</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-sm space-y-1">
                <div>La tua risposta: <span className="font-mono-num font-bold">{d.your_answer || "—"}</span>
                  {d.your_answer && typeof d.options[d.your_answer] === "string" && !d.options[d.your_answer].startsWith("<svg") && (
                    <span className="ml-2 text-slate-600">— {d.options[d.your_answer]}</span>
                  )}
                </div>
                <div>Risposta corretta: <span className="font-mono-num font-bold text-[#002FA7]">{d.correct_answer}</span>
                  {typeof d.options[d.correct_answer] === "string" && !d.options[d.correct_answer].startsWith("<svg") && (
                    <span className="ml-2">— {d.options[d.correct_answer]}</span>
                  )}
                </div>
                {typeof d.options[d.correct_answer] === "string" && d.options[d.correct_answer].startsWith("<svg") && (
                  <div className="flex gap-3 items-center mt-2 label-mono text-xs text-slate-500">
                    <span>Corretta:</span>
                    <div className="w-14 h-14 border border-[#002FA7]" dangerouslySetInnerHTML={{ __html: d.options[d.correct_answer] }} />
                  </div>
                )}
              </div>
              {d.explanation && (
                <div className="mt-4 pt-4 border-t border-slate-200 text-sm text-slate-600">
                  <span className="label-mono text-slate-400">{t("explanation")}: </span>{d.explanation}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 flex gap-4">
          <Link to="/dashboard"><Button className="rounded-none bg-[#002FA7] hover:bg-[#001d6b] label-mono py-6">Torna alla dashboard</Button></Link>
          <Link to="/pricing"><Button variant="outline" className="rounded-none border-slate-900 label-mono py-6">Vedi altri test</Button></Link>
        </div>
      </div>
    </Layout>
  );
}
